<template>
    <div class="chat-box-body" :class="{'chat-box-body_padding-top': user}" ref="messages-list" id="chat-messages-list">
        <div v-if="fetcher.pageLoading" class="chat-top-spinner">
            <spinner></spinner>
        </div>
        <template v-if="!fetcher.loading">
            <div v-for="(list, date) in messages">
                <div class="message-date" :class="{scrolling: scrolling}">
                        <span class="date">
                            {{date}}
                        </span>
                </div>
                <div class="messages-group" v-for="msgs in list">
                    <template v-for="(msg, index) in msgs">
                        <message v-if="msg.isRegular()"
                                 :id="msg.id"
                                 :key="msg.id"
                                 :message="msg"
                                 :first="index === 0"
                                 :last="index == msgs.length - 1"
                                 v-on:resend="resend"
                                 @sendContacts="$emit('sendContacts')"
                        />
                        <plain-message v-else
                                       :id="msg.id"
                                       :key="msg.id"
                                       :message="msg"
                        />
                    </template>
                </div>
            </div>
            <div v-if="Object.keys(messages).length === 0" class="chat-empty-message">
                {{ $t('common.no messages') }}
            </div>
        </template>
        <transition name="fade">
            <div v-if="fetcher.loading" class="chat-bottom-spinner">
                <spinner></spinner>
            </div>
        </transition>
    </div>
</template>

<script>
import { Vue } from 'vue-property-decorator';
import Spinner from "@share/chat/components/Spinner";
import PlainMessage from "@share/chat/components/chat/PlainMessage";
import Message from "@share/chat/components/chat/Message";
import MessagesHelper from "@share/chat/helpers/MessagesHelper";
import config from "@share/chat/config";
import {EventBus} from "../../helpers/EventBus";
import uniq from "uniq";
import debounce from "lodash.debounce";
import { MessagesFetcher } from "@share/chat/helpers/MessagesFetcher";

export default Vue.extend({
    name: "Messages",
    components: {Message, PlainMessage, Spinner},
    props: {
        user: {
            type: Object,
            required: true
        },
        room: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            page: 1,
            messageHelper: new MessagesHelper(),
            seenTimer: null,
            seenIds: [],
            alreadySeen: [],
            scrolling: false,
            scrollingTimer: null,
            showDate: true,
            fetcher: new MessagesFetcher(this, config)
        }
    },
    computed: {
        messages() {
            return this.messageHelper.prepareMessages(this.$store.state.chat.room.messages, this.lang);
        },
        lang() {
            return this.$store.state.session.language || 'en'
        },
    },
    methods: {
        resend(message) {
            this.$emit('resend', message);
        },
        async fetchMessages() {
            this.$emit('fetchingStarted');
            const result = await this.fetcher.fetchMessages();
            if (result) {
                this.$emit('fetchingEnded');

                this.$nextTick(() => {
                    this.scrollDown();
                    if (this.$refs['messages-list'] && this.$refs['messages-list'].clientHeight == this.$refs['messages-list'].scrollHeight) { //in case there is no scroll
                        this.onScroll();
                    }
                })
            }
        },
        //@todo move scroll logic to a separate class
        onScroll() {
            clearTimeout(this.scrollingTimer);
            if (this.showDate) {
                this.scrolling = true;
            }
            this.scrollingTimer = setTimeout(() => {
                this.scrolling = false;
                this.showDate = true;
            }, 500);
            clearTimeout(this.seenTimer);
            let ids = [],
                firstUnreadElement = null;

            if (this.$store.state.chat.room.instance.unreadCount) {
                for (let messageIndex = this.$store.state.chat.room.messages.length - 1; messageIndex >= 0; messageIndex--) {
                    let item = this.$store.state.chat.room.messages[messageIndex];
                    if (!item.isSeen && (item.userID != this.currentUserId || item.type != 1)) {
                        if (firstUnreadElement && item.createdAt <= firstUnreadElement.createdAt) {
                            ids.push(item.id);
                        } else {
                            const element = document.getElementById(item.id);
                            if (this.alreadySeen.indexOf(item.id) < 0 && element && this.isInViewport(this.$refs['messages-list'], element)) {
                                ids.push(item.id);
                                firstUnreadElement = item;
                            }
                        }
                    }
                }
            }
            if (ids.length) {
                this.seenIds = this.seenIds.concat(ids);
                this.sendSeen();
            }
        },
        onScrollLoad: debounce(function () {
            if (this.$refs && this.$refs['messages-list'] && this.$refs['messages-list'].scrollTop === 0 && !this.fetcher.pageLoading) {
                const previousLength = this.$store.state.chat.room.messages.length;

                this.fetcher.fetchPage()
                .then((lastMessageID) => {
                    const currentLength = this.$store.state.chat.room.messages.length;
                    if (previousLength != currentLength && lastMessageID) {
                        const position = document.getElementById(lastMessageID).offsetTop;
                        this.$refs['messages-list'].scrollTop = position - 30;
                    }
                });
            }
        }, 500),
        scrollDown() {
            if (this.$refs['messages-list'] && this.$refs['messages-list'].clientHeight > 0) {
                this.showDate = false;
                this.$refs['messages-list'].scrollTop = this.$refs['messages-list'].scrollHeight;
            }
            else {
                setTimeout(() => {
                    this.scrollDown();
                }, 350)
            }
        },
        //@todo  make already seen a seprate logic
        sendSeen() {
            const roomId = this.room.id;
            this.seenTimer = setTimeout(() => {
                const ids = uniq(this.seenIds);
                if (ids && ids.length) {
                    this.alreadySeen = this.alreadySeen.concat(ids);
                    this.$store.state.chat.socket.openMessage(ids);
                    this.$store.dispatch('chat/room/setIsSeen', ids);
                    this.$store.dispatch('chat/rooms/setIsSeen', {
                        roomId: roomId,
                        messageIds: ids
                    });
                    this.seenIds = [];
                }
            }, 500);
        },
        isInViewport(parent, elem) {
            const start = this.$refs['messages-list'].scrollTop,
                end = this.$refs['messages-list'].scrollTop + this.$refs['messages-list'].clientHeight;
            if (elem.offsetTop >= start && elem.offsetTop <= end) {
                return true
            }
            return false;
        },
        receiveMessage(message) {
            if (this.$refs['messages-list'].scrollTop + this.$refs['messages-list'].clientHeight == this.$refs['messages-list'].scrollHeight) {
                this.$nextTick(() => {
                    this.scrollDown();
                });
            }
        },
    },
    beforeDestroy() {
        if (this.$refs['messages-list']) {
            this.$refs['messages-list'].removeEventListener('scroll', this.onScrollLoad);
            this.$refs['messages-list'].removeEventListener('scroll', this.onScroll);
        }
        EventBus.$off('receiveMessage', this.receiveMessage);
    },
    mounted() {
        this.$refs['messages-list'].addEventListener('scroll', this.onScrollLoad);
        this.$refs['messages-list'].addEventListener('scroll', this.onScroll);
        this.onScroll();
        this.fetchMessages();
        EventBus.$on('receiveMessage', this.receiveMessage);
    }
})
</script>

<style scoped>

</style>
